import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';

const GREEN = '#4EA14C';

const RESET_SCHEME = 'zemaitijoskeliais';
const RESET_PATH = 'reset-password';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    minHeight: '100vh',
    margin: 0,
    padding: 0,
    backgroundColor: '#000',
    color: '#fff',
    boxSizing: 'border-box',
    overflow: 'auto',
  },
  content: {
    maxWidth: 600,
    margin: '0 auto',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    boxSizing: 'border-box',
  },
  section: {
    marginBottom: theme.spacing(2),
    color: '#fff',
  },
  textSecondary: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  openInAppButton: {
    marginTop: theme.spacing(2),
    backgroundColor: GREEN,
    color: '#fff',
    borderRadius: 28,
    '&:hover': {
      backgroundColor: GREEN,
      opacity: 0.9,
    },
  },
}));

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function buildResetPasswordUrl(code) {
  const base = `${RESET_SCHEME}://${RESET_PATH}`;
  if (!code) return base;
  const params = new URLSearchParams({ code });
  return `${base}?${params.toString()}`;
}

const FORGOT_PASSWORD_TEXTS = {
  'lt-LT': {
    title: 'Slaptažodžio atkūrimas',
    redirecting: 'Nukreipiama į programėlę...',
    openInApp: 'Atidaryti programėlėje',
    missingCode: 'Trūksta atkūrimo kodo. Naudokite nuorodą iš el. laiško.',
    example: 'Pavyzdys:',
  },
  'en-EU': {
    title: 'Reset password',
    redirecting: 'Redirecting to the app...',
    openInApp: 'Open in app',
    missingCode: 'Reset code is missing. Use the link from your email.',
    example: 'Example:',
  },
};

const ForgotPasswordPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const locale = location.pathname.startsWith('/en-EU/') ? 'en-EU' : 'lt-LT';
  const t = FORGOT_PASSWORD_TEXTS[locale] || FORGOT_PASSWORD_TEXTS['en-EU'];
  const query = useQuery();
  const code = (query.get('code') || '').trim();

  const resetPasswordUrl = buildResetPasswordUrl(code);
  const forgotPath = `/${locale}/forgot-password`;

  useEffect(() => {
    const prevBodyBg = document.body.style.backgroundColor;
    const prevBodyMargin = document.body.style.margin;
    const prevBodyPadding = document.body.style.padding;
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = '#000';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.backgroundColor = '#000';
    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.body.style.margin = prevBodyMargin;
      document.body.style.padding = prevBodyPadding;
      document.documentElement.style.backgroundColor = prevHtmlBg;
    };
  }, []);

  // Auto-redirect to app when code is present
  useEffect(() => {
    if (!code) return;
    window.location.href = resetPasswordUrl;
  }, [code, resetPasswordUrl]);

  if (!code) {
    return (
      <Box className={classes.root}>
        <Box className={classes.content}>
          <Typography variant="h5" className={classes.section}>
            {t.title}
          </Typography>
          <Typography variant="body2" className={classes.textSecondary}>
            {t.missingCode}
          </Typography>
          <Typography
            variant="body2"
            className={classes.textSecondary}
            style={{ marginTop: 8 }}
          >
            {t.example} {forgotPath}?code=YOUR_RESET_CODE
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Typography variant="h5" className={classes.section}>
          {t.title}
        </Typography>
        <Typography variant="body2" className={classes.textSecondary}>
          {t.redirecting}
        </Typography>
        <Button
          className={classes.openInAppButton}
          variant="contained"
          component="a"
          href={resetPasswordUrl}
        >
          {t.openInApp}
        </Button>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
